import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {PricingSectionSliceDefault, PricingSectionSliceReskin} from 'prismicio-types'
import {PackageSection} from 'slices/PricingSection/components/PackageSection'
import {VariationLink} from 'slices/PricingSection/components/VariationLink'
import {Container} from 'src/common/Container'
import type {Context} from 'src/common/types'

/**
 * Props for `PricingSection`.
 */
export type PricingDefaultSectionProps = {
  slice: (PricingSectionSliceDefault | PricingSectionSliceReskin) & {
    slice_type: string
    variation: string
  }
  context: {
    pageUid: string
    buttonsData: Context['buttonsData']
  }
  componentIndex: number
}

/**
 * Component for "PricingSection" Slices.
 */
export const PricingSectionDefault = ({
  slice,
  componentIndex,
  context: {pageUid, buttonsData},
}: PricingDefaultSectionProps): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const stealthLinks = 'stealth_links' in slice.primary && slice.primary.stealth_links

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      backgroundColor={slice.variation === 'reskin' ? 'Grey/100' : 'Beige/100'}
      id="pricing-section"
    >
      <div className="grid-base max-w-[1200px] gap-y-14 px-5 md:px-10 lg:gap-y-[96px] lg:px-5 xl:px-0">
        <div className="col-span-full flex flex-col gap-4 md:col-span-10 md:col-start-2 lg:col-span-6 lg:col-start-4">
          {isFilled.keyText(slice.primary.title) && (
            <h3 className="text-h3 text-center text-moss-black">{slice.primary.title}</h3>
          )}
          {isFilled.richText(slice.primary.subtext) && (
            <PrismicRichText
              field={slice.primary.subtext}
              components={{
                paragraph: ({children}) => (
                  <p className="text-tg-body-m md:text-tg-body-l text-center text-grey-550">{children}</p>
                ),
                hyperlink: ({children, node}) => (
                  <Link
                    className={cn('inline transition-colors hover:text-7290f3', {
                      'text-inherit': stealthLinks,
                      'text-7290f3': !stealthLinks,
                    })}
                    href={node.data.url || linkResolver(node.data)}
                    {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                    {...(node.data.link_type === 'Web' &&
                      node.data.target === '_blank' && {rel: 'noopener noreferrer'})}
                  >
                    {children}
                  </Link>
                ),
              }}
            />
          )}
          <VariationLink
            variation={slice.variation}
            componentIndex={componentIndex}
            pageUid={pageUid}
            buttonsData={buttonsData}
            {...(slice.variation === 'default'
              ? {
                  label: slice.primary.text_link_label,
                  url: slice.primary.text_link_url,
                }
              : {
                  primaryButton: slice.primary.primary_button,
                  secondaryButton: slice.primary.secondary_button,
                })}
          />
        </div>
        <div className="col-span-full grid grid-cols-subgrid gap-y-10">
          <PackageSection
            title={slice.primary.first_package_title}
            modules={slice.primary.first_package}
            variation={slice.variation}
            enableContainer
          />
          <PackageSection
            title={slice.primary.second_package_title}
            modules={slice.primary.second_package}
            variation={slice.variation}
          />
          <PackageSection
            title={slice.primary.third_package_title}
            modules={slice.primary.third_package}
            variation={slice.variation}
          />
        </div>
      </div>
    </Container>
  )
}
